import {
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Button,
  TextField,
  FormControl,
  Typography,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import {
  DatePicker,
  LocalizationProvider,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Close as CloseIcon } from "@mui/icons-material";

import { useContext, useState } from "react";
import { Form, Formik } from "formik";
import * as yup from "yup";
import dayjs from "dayjs";

import {
  employmentTypes,
  industryTypes,
  isRequiredString,
  postWalkInLabelNames,
  postWalkInSuccessMsg,
  walkInCategories,
  walkInExperiences,
} from "../../constant";
import { FunctionContext } from "../../context/FunctionProvider";

import WalkInStepper from "../WalkInStepper";
import WalkInFormLabel from "../formfields/WalkInFormLabel";
import WalkInHelperText from "../formfields/WalkInHelperText";
import WalkInSelect from "../formfields/WalkInSelect";
import WalkInFormControlTextField from "../formfields/WalkInFormControlTextField";
import SupabaseService from "../../utils/supabaseClient";

import "../styles/core-element.css";
import WalkInTimePicker from "../formfields/WalkInTimePicker";

function PostWalkInDialog({ openPostWalkInDialog, setOpenPostWalkInDialog }) {
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const steps = ["Profile Summary", "Contact Details"];
  const handleNext = () => {
    setActiveStep((prevActiveStep) =>
      prevActiveStep >= 2 ? prevActiveStep : prevActiveStep + 1
    );
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const initialValues = {
    walkInTitle: "",
    walkInLocation: "",
    interviewDate: dayjs(),
    interviewTimeFrom: dayjs(),
    interviewTimeTo: dayjs(),
    experience: "",
    walkInCategory: "",
    walkInDescription: "",
    companyName: "",
    mapLocationLink: "",
    industryType: "",
    employmentType: "",
    email: "",
    contactNumber: "",
  };
  const validationSchema = yup.object({
    walkInTitle: yup
      .string()
      .required(isRequiredString(postWalkInLabelNames.walkInTitle)),
    companyName: yup
      .string()
      .required(isRequiredString(postWalkInLabelNames.companyName)),
    walkInLocation: yup
      .string()
      .required(isRequiredString(postWalkInLabelNames.walkInLocation)),
    interviewDate: yup
      .date()
      .required(isRequiredString(postWalkInLabelNames.interviewDate)),
    interviewTimeFrom: yup
      .date()
      .required(isRequiredString(postWalkInLabelNames.interviewTime)),
    interviewTimeTo: yup
      .date()
      .required(isRequiredString(postWalkInLabelNames.interviewTime)),
    mapLocationLink: yup
      .string()
      .required(isRequiredString(postWalkInLabelNames.mapLocationLink)),
    experience: yup
      .string()
      .required(isRequiredString(postWalkInLabelNames.experience)),
    industryType: yup
      .string()
      .required(isRequiredString(postWalkInLabelNames.industryType)),
    employmentType: yup
      .string()
      .required(isRequiredString(postWalkInLabelNames.employmentType)),
    walkInCategory: yup
      .string()
      .required(isRequiredString(postWalkInLabelNames.walkInCategory)),
    walkInDescription: yup
      .string()
      .required(isRequiredString(postWalkInLabelNames.walkInDescription)),
    email: yup
      .string()
      .email()
      .matches(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, "Invalid email")
      .required(isRequiredString(postWalkInLabelNames.email)),
    contactNumber: yup
      .number()
      .min(1000000000, "Not a Valid number")
      .max(9999999999, "Not a Valid number")
      .required(isRequiredString(postWalkInLabelNames.contactNumber)),
  });
  const { customBreakpoints, openSnack, setOpenSnack } =
    useContext(FunctionContext);
  const setOpenSnackBar = (snackMessage, type = "success") => {
    setOpenSnack({
      ...openSnack,
      open: true,
      message: snackMessage,
      type: type,
    });
  };
  const handleSubmit = async (values) => {
    if (activeStep === 2) {
      setIsLoading(true);

      try {
        values.interviewDate = dayjs(values.interviewDate).format("MM-DD-YYYY");
        values.interviewTimeFrom = dayjs(values.interviewTimeFrom).format(
          "hh:mm A"
        );
        values.interviewTimeTo = dayjs(values.interviewTimeTo).format(
          "hh:mm A"
        );
        await SupabaseService.insertData("postWalkin", values);
        setIsLoading(false);
        setOpenPostWalkInDialog(false);
        setOpenSnackBar(postWalkInSuccessMsg);
      } catch (e) {
        setIsLoading(false);
        setOpenPostWalkInDialog(false);
        setOpenSnackBar(e.message, "error");
      }
    }
    // onClose();
  };

  return (
    <>
      <Dialog
        sx={{
          "& .MuiPaper-root": {
            // backgroundColor: "#339fcd",
            borderRadius: "10px",
            maxWidth: "760px",
          },
        }}
        open={openPostWalkInDialog}
        onClose={() => setOpenPostWalkInDialog(false)}
        fullWidth
        fullScreen={
          customBreakpoints.isMobile || customBreakpoints.isDeviceDown
        }
      >
        <DialogContent>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: "25px",
            }}
          >
            <Box>
              <Typography
                variant={
                  customBreakpoints.isMobile || customBreakpoints.isDeviceDown
                    ? "h6"
                    : "h5"
                }
                fontFamily={"Poppins-SemiBold"}
              >
                Post your Walk-ins Free!!
              </Typography>{" "}
              <IconButton
                aria-label="close"
                onClick={() => {
                  setOpenPostWalkInDialog(false);
                  setActiveStep(0);
                }}
                sx={(theme) => ({
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: theme.palette.grey[500],
                })}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Box my={2.5}>
              <WalkInStepper activeStep={activeStep} steps={steps} />
            </Box>

            <Box>
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  values,
                  setFieldValue,
                  isValid,
                }) => (
                  <Form>
                    <Box
                      display={"flex"}
                      flexDirection={"column"}
                      width={"100%"}
                    >
                      {activeStep === 0 && (
                        <Box width={"100%"}>
                          <WalkInFormControlTextField
                            labelName={postWalkInLabelNames.walkInTitle}
                            name="walkInTitle"
                            placeholder="Ex,Java Developer"
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            value={values.walkInTitle}
                            helperText={errors.walkInTitle}
                            errors={touched.walkInTitle && errors.walkInTitle}
                          />
                          <WalkInFormControlTextField
                            labelName={postWalkInLabelNames.walkInLocation}
                            name={"walkInLocation"}
                            placeholder={"Chennai,Tamil Nadu"}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            value={values.walkInLocation}
                            helperText={errors.walkInLocation}
                            errors={
                              touched.walkInLocation && errors.walkInLocation
                            }
                          />
                          <div style={{ width: "100%" }}>
                            <FormControl
                              fullWidth={
                                customBreakpoints.isMobile ||
                                customBreakpoints.isDeviceDown
                              }
                            >
                              <WalkInFormLabel
                                labelName={postWalkInLabelNames.interviewDate}
                              />
                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                  disablePast
                                  name="interviewDate"
                                  value={dayjs(values.interviewDate)}
                                  onChange={(e) => {
                                    handleChange(dayjs(e).format());
                                  }}
                                  onBlur={handleBlur}
                                />
                              </LocalizationProvider>
                              <WalkInHelperText
                                helperText={errors.interviewDate}
                                errors={
                                  touched.interviewDate && errors.interviewDate
                                }
                              />
                            </FormControl>
                          </div>
                          <div style={{ width: "100%" }}>
                            <WalkInFormLabel
                              labelName={postWalkInLabelNames.interviewTime}
                            />
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "space-between",
                                gap: "10px",
                              }}
                            >
                              <WalkInTimePicker
                                name={"interviewTimeFrom"}
                                handleBlur={handleBlur}
                                value={values.interviewTimeFrom}
                                setFieldValue={setFieldValue}
                                helperText={errors.interviewTimeFrom}
                                errors={
                                  touched.interviewTimeFrom &&
                                  errors.interviewTimeFrom
                                }
                              />
                              <span
                                style={{
                                  fontFamily: "Poppins-Regular",
                                  marginTop: "16px",
                                }}
                              >
                                to
                              </span>
                              <WalkInTimePicker
                                name={"interviewTimeTo"}
                                handleBlur={handleBlur}
                                value={values.interviewTimeTo}
                                setFieldValue={setFieldValue}
                                helperText={errors.interviewTimeTo}
                                errors={
                                  touched.interviewTimeTo &&
                                  errors.interviewTimeTo
                                }
                              />
                            </div>
                          </div>
                          <WalkInSelect
                            labelName={postWalkInLabelNames.experience}
                            fieldName={"experience"}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            values={values.experience}
                            touched={touched.experience}
                            errors={errors.experience}
                            options={walkInExperiences}
                          />
                          <WalkInSelect
                            labelName={postWalkInLabelNames.walkInCategory}
                            fieldName={"walkInCategory"}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            values={values.walkInCategory}
                            touched={touched.walkInCategory}
                            errors={errors.walkInCategory}
                            options={walkInCategories}
                          />
                          <FormControl fullWidth>
                            <WalkInFormLabel
                              labelName={postWalkInLabelNames.walkInDescription}
                            />
                            <TextField
                              name="walkInDescription"
                              fullWidth
                              placeholder="Walk-in Description..."
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.walkInDescription}
                              multiline
                              rows={4}
                            />
                            <WalkInHelperText
                              helperText={errors.walkInDescription}
                              errors={
                                touched.walkInDescription &&
                                errors.walkInDescription
                              }
                            />
                          </FormControl>
                        </Box>
                      )}
                      {activeStep >= 1 && (
                        <Box width={"100%"}>
                          {/* Contact Details */}

                          <WalkInFormControlTextField
                            labelName={postWalkInLabelNames.companyName}
                            name={"companyName"}
                            placeholder={"Enter Company Name"}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            value={values.companyName}
                            helperText={errors.companyName}
                            errors={touched.companyName && errors.companyName}
                          />
                          <WalkInSelect
                            labelName={postWalkInLabelNames.industryType}
                            fieldName={"industryType"}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            values={values.industryType}
                            touched={touched.industryType}
                            errors={errors.industryType}
                            options={industryTypes}
                          />
                          <WalkInSelect
                            labelName={postWalkInLabelNames.employmentType}
                            fieldName={"employmentType"}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            values={values.employmentType}
                            touched={touched.employmentType}
                            errors={errors.employmentType}
                            options={employmentTypes}
                          />

                          <WalkInFormControlTextField
                            labelName={postWalkInLabelNames.email}
                            name={"email"}
                            type={"email"}
                            placeholder={"Enter Email"}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            value={values.email}
                            helperText={errors.email}
                            errors={touched.email && errors.email}
                          />
                          <WalkInFormControlTextField
                            labelName={postWalkInLabelNames.contactNumber}
                            name={"contactNumber"}
                            type={"number"}
                            placeholder={"Enter Contact Number"}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            value={values.contactNumber}
                            helperText={errors.contactNumber}
                            errors={
                              touched.contactNumber && errors.contactNumber
                            }
                          />
                          <WalkInFormControlTextField
                            labelName={postWalkInLabelNames.mapLocationLink}
                            name={"mapLocationLink"}
                            placeholder={"Exact map location link"}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            value={values.mapLocationLink}
                            helperText={errors.mapLocationLink}
                            errors={
                              touched.mapLocationLink && errors.mapLocationLink
                            }
                          />
                        </Box>
                      )}
                    </Box>
                    <DialogActions>
                      <Box display={"flex"} justifyContent={"end"} gap={1}>
                        <Button
                          variant="outlined"
                          className="secondary-btn"
                          disabled={activeStep === 0}
                          onClick={handleBack}
                        >
                          Back
                        </Button>
                        <Button
                          disabled={
                            activeStep === 1 ? (isValid ? false : true) : false
                          }
                          variant="contained"
                          className="primary-btn"
                          onClick={handleNext}
                          type="submit"
                        >
                          {" "}
                          {activeStep !== 0 ? "Submit" : "Next"}
                        </Button>
                      </Box>
                    </DialogActions>
                    <Backdrop
                      open={isLoading}
                      sx={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        color: "#ffffff",
                      }}
                    >
                      {" "}
                      <CircularProgress color="inherit" />
                    </Backdrop>
                  </Form>
                )}
              </Formik>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default PostWalkInDialog;
